import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Keyboard } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { ComposerTermsReveal } from './ComposerTermsReveal';
import { useAskConversationContext, useAskUiStoreApi } from './LibraryChatContext';
import { ResearchTourZone } from './researchTutorial/ResearchTourZone';
import { useResearchTutorialActions } from './researchTutorial/researchTutorialContext';
import { SEND_FAILURE_TITLE, sendFailureMessage } from './sendFailureMessages';

import { ChatComposerInput } from '@/components/composer/ChatComposerInput';
import { ConfirmationCheckbox } from '@/components/form/ConfirmationCheckbox';
import { GlassSurface, useLiquidGlass } from '@/components/ui/glass';
import { HStack } from '@/components/ui/hstack';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { useToaster } from '@/hooks/useToaster';
import { ASK_TEXT_MAX_LENGTH, ASK_TEXT_MIN_LENGTH } from '@/services/ask/api';

export type LibraryChatComposerHandle = {
  setText: (text: string) => void;
};

/** Show the counter once the reader is within this many characters of the limit. */
const COUNTER_THRESHOLD = 500;

/**
 * Why Send is off, for the button's screen-reader hint. No caption under the
 * box says it, so without this a screen reader meets a dead control.
 */
function sendUnavailableReason({
  isBusy,
  isTooShort,
  needsTerms,
}: {
  isBusy: boolean;
  isTooShort: boolean;
  needsTerms: boolean;
}): string | undefined {
  if (isBusy) {
    return 'Available when the current search finishes';
  }

  if (isTooShort) {
    return `Questions need at least ${ASK_TEXT_MIN_LENGTH} characters`;
  }

  if (needsTerms) {
    return 'Check the terms box above first';
  }

  return undefined;
}

/**
 * The box's prompt. Once the topic holds a question, the next message adds to
 * that search, so the prompt asks for a follow-up rather than a new question.
 */
function composerPlaceholder({ isBusy, hasAsked }: { isBusy: boolean; hasAsked: boolean }) {
  if (isBusy) {
    return 'Searching…';
  }

  return hasAsked ? 'Add detail or ask a follow-up' : 'Ask a question or describe a case';
}

/** A 2pt ring round the 40pt send button, so the tutorial's highlight around it is a circle. */
const SEND_HIGHLIGHT_PADDING = { x: 2, y: 2 };

/** Half the padded button's width. */
const SEND_HIGHLIGHT_RADIUS = 22;

/**
 * The question box.
 *
 * Send stays disabled below the minimum length and while a turn runs in this
 * conversation, on this device or another, since the server accepts one
 * running turn at a time. Nothing is captioned under the box.
 *
 * Patient identifiers are screened by the server alone. A send it refuses,
 * for that or any other reason, puts its text back and says why in a toast;
 * a full topic instead starts a new one carrying the question.
 * Sending that same text again reuses the failed attempt's turn id, so a
 * question that did land is never asked twice.
 *
 * Typing and Enter-to-send come from the shared composer input. Native
 * closes the keyboard on send so the search's progress and results are in
 * view.
 *
 * Where Liquid Glass is drawn, the box and the character counter are
 * one glass panel, since the screen floats the composer over the thread and
 * loose text there would sit on top of the answers. Elsewhere it keeps its
 * white box on the screen's tinted background.
 *
 * A new topic's first send also holds on the terms checkbox docked above the
 * box. It stays out of the way until the reader first types, then opens above
 * the box, and it closes as a send goes out, since the acknowledgement travels
 * with that attempt. A refused send brings it back with the text, still
 * checked. It has no dismiss control by design.
 */
export const LibraryChatComposer = forwardRef<LibraryChatComposerHandle>(
  function LibraryChatComposer(_props, ref) {
    const { send, activeTurn, isSending, isNew, snapshot, startNewWithDraft, conversationId } =
      useAskConversationContext();

    const tutorial = useResearchTutorialActions();

    const store = useAskUiStoreApi();

    const [text, setText] = useState('');

    const hasGlass = useLiquidGlass();

    const toaster = useToaster();

    const [agreedToTerms, setAgreedToTerms] = useState(false);

    const [hasStartedTyping, setHasStartedTyping] = useState(false);

    if (text !== '' && !hasStartedTyping) {
      setHasStartedTyping(true);
    }

    useImperativeHandle(ref, () => ({ setText }), []);

    useEffect(() => {
      if (!isNew) {
        return;
      }

      const draft = store.getState().takeHandoffDraft();

      if (draft) {
        setText(draft);
      }
    }, [isNew, store]);

    const trimmed = text.trim();

    const isTooShort = trimmed.length < ASK_TEXT_MIN_LENGTH;

    const isBusy = activeTurn != null || isSending;

    const needsTerms = isNew && !agreedToTerms;

    const showsTerms = isNew && hasStartedTyping && !isBusy;

    const hasAsked = (snapshot?.turns.length ?? 0) > 0;

    const canSend = !isBusy && !isTooShort && !needsTerms;

    const remaining = ASK_TEXT_MAX_LENGTH - text.length;

    const sendHint = sendUnavailableReason({ isBusy, isTooShort, needsTerms });

    const trySend = () => {
      if (!canSend) {
        return;
      }

      const question = trimmed;

      setText('');

      if (!isWeb) {
        Keyboard.dismiss();
      }

      // Only a new topic's first question can be the tutorial's. A follow-up in
      // a saved conversation is the reader working outside the tour.
      if (isNew) {
        tutorial.sendStarted(conversationId);
      }

      send(question, { termsAccepted: agreedToTerms })
        .then((error) => {
          if (isNew) {
            tutorial.sendSettled(conversationId, !error);
          }

          if (!error || error.kind === 'NOT_FOUND') {
            return;
          }

          if (error.kind === 'CONVERSATION_LIMIT_REACHED') {
            startNewWithDraft(question);

            return;
          }

          setText((current) => (current === '' ? question : current));

          toaster.showError({ title: SEND_FAILURE_TITLE, message: sendFailureMessage(error.kind) });
        })
        .catch(() => {
          if (isNew) {
            tutorial.sendSettled(conversationId, false);
          }
        });
    };

    return (
      <View className={twJoin('w-full', hasGlass && 'rounded-[28px] shadow-soft-2')}>
        <GlassSurface isInteractive className={twJoin('w-full', hasGlass && 'rounded-[28px]')}>
          <ComposerTermsReveal isVisible={showsTerms}>
            <View className={twJoin('pb-1', hasGlass ? 'px-4 pt-4' : 'px-2')}>
              <ResearchTourZone segment="compose" zone={2} canAdvance={agreedToTerms}>
                <ConfirmationCheckbox
                  variant="research"
                  isChecked={agreedToTerms}
                  onChange={setAgreedToTerms}
                  isDisabled={isBusy}
                />
              </ResearchTourZone>
            </View>
          </ComposerTermsReveal>

          <ResearchTourZone segment="compose" zone={1}>
            <ChatComposerInput
              value={text}
              onChangeText={setText}
              onSubmit={trySend}
              canSubmit={canSend}
              placeholder={composerPlaceholder({ isBusy, hasAsked })}
              maxLength={ASK_TEXT_MAX_LENGTH}
              nativeID="libraryChatComposer"
              inputLabel="Ask a question or add a follow-up"
              sendLabel="Send question"
              sendHint={sendHint}
              wrapSendButton={(sendButton) => (
                <ResearchTourZone
                  segment="compose"
                  zone={3}
                  action={trySend}
                  canAdvance={canSend}
                  highlightRadius={SEND_HIGHLIGHT_RADIUS}
                  highlightPadding={SEND_HIGHLIGHT_PADDING}>
                  {sendButton}
                </ResearchTourZone>
              )}
            />
          </ResearchTourZone>

          {remaining <= COUNTER_THRESHOLD ? (
            <HStack className={twJoin('mt-1 justify-end', hasGlass ? 'px-4 pb-2' : 'px-2')}>
              <Text className="text-xs text-typography-700" accessibilityLiveRegion="polite">
                {remaining} left
              </Text>
            </HStack>
          ) : null}
        </GlassSurface>
      </View>
    );
  }
);
